import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { ConfirmDialog } from "molecules/dialog/ConfirmDialog";
import { LoadingState } from "molecules/loadingState/LoadingState";
import { MissingDataState } from "molecules/missingDataState/MissingDataState";
import { useTranslation } from "react-i18next";
import type { AccessGroup } from "../../common/service/types/AccessGroup.ts";
import {
	makeArrayLengthSort,
	makeStringSort,
	useSort,
} from "../../common/utils/hooks/useSort";
import { DataTable } from "molecules/data-table/DataTable";
import { SortHeaderButton } from "molecules/sortAndFilterHeader/SortHeaderButton";
import { Anchor } from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { IconTrash } from "@tabler/icons-react";
import { getGroupName } from "./getGroupName";
import { GroupColorDot } from "./userCells";
import { useState } from "react";
import {
	useDeleteAccessGroupMutation,
	useGetAccessGroupsQuery,
} from "../../common/redux/api/exopenApi";

import { AddAccessGroupModal } from "./AddAccessGroupModal";

import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.ts";

export const AccessGroupTable = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const { data: accessGroups, isLoading: isLoadingAccessGroups } =
		useGetAccessGroupsQuery(companyDomain.id);
	const groupById: Record<number, AccessGroup> = {};
	for (const group of accessGroups ?? []) {
		groupById[group.groupId] = group;
	}
	const { items: sortedGroups, sortColumnProps } = useSort({
		keys: ["byName", "byUsers"],
		defaultKey: "byName",
		defaultOrder: "asc",
		rows: accessGroups ?? [],
		columns: {
			byName: {
				sort: makeStringSort("name"),
			},
			byUsers: {
				sort: makeArrayLengthSort("userEmails"),
			},
		},
	});
	const [groupToEdit, setGroupToEdit] = useState<number>();
	const [groupToDelete, setGroupToDelete] = useState<number>();
	const [deleteAccessGroup, { isLoading: isDeletingAccessGroup }] =
		useDeleteAccessGroupMutation();

	if (isLoadingAccessGroups) {
		return <LoadingState />;
	}

	if (!accessGroups) {
		return <MissingDataState />;
	}

	return (
		<>
			<DataTable
				label={t("Groups")}
				rows={sortedGroups}
				getRowKey={(accessGroup) => `${accessGroup.groupId}`}
				columns={[
					{
						id: "name",
						width: "auto",
						header: (
							<SortHeaderButton
								label={t("Group name")}
								activeDirection={
									sortColumnProps.byName.selected
										? sortColumnProps.byName.order
										: null
								}
								onToggle={sortColumnProps.byName.onClick}
							/>
						),
						render: (accessGroup) => (
							<Anchor<"a">
								onClick={(event) => {
									event.preventDefault();
									setGroupToEdit(accessGroup.groupId);
								}}
							>
								{getGroupName({ t, groupName: accessGroup.name })}
							</Anchor>
						),
					},
					{
						id: "description",
						width: "auto",
						header: t("Description"),
						render: (accessGroup) => accessGroup.description,
					},
					{
						id: "users",
						width: "auto",
						header: (
							<SortHeaderButton
								label={t("Number of users")}
								activeDirection={
									sortColumnProps.byUsers.selected
										? sortColumnProps.byUsers.order
										: null
								}
								onToggle={sortColumnProps.byUsers.onClick}
							/>
						),
						render: (accessGroup) => accessGroup.userEmails.length,
					},
					{
						id: "colour",
						width: "auto",
						header: t("Colour"),
						render: (accessGroup) => (
							<GroupColorDot colorCode={accessGroup.colorCode} />
						),
					},
					{
						id: "actions",
						width: "min-content",
						render: (accessGroup) => (
							<Button
								aria-label={t("Remove group")}
								onClick={(e) => {
									e.stopPropagation();
									setGroupToDelete(accessGroup.groupId);
								}}
								variant="ghost"
								disabled={accessGroup.readOnly}
								icon={<IconTrash />}
							/>
						),
					},
				]}
			/>
			<AddAccessGroupModal
				accessGroup={groupToEdit ? groupById[groupToEdit] : undefined}
				modalOpen={groupToEdit !== undefined}
				handleClose={() => {
					setGroupToEdit(undefined);
				}}
				companyId={companyDomain.id}
			/>
			<ConfirmDialog
				open={groupToDelete !== undefined}
				onClose={() => {
					setGroupToDelete(undefined);
				}}
				title={t("Remove group")}
				description={t("Do you want to delete the group?")}
				onConfirm={async () => {
					const result = await deleteAccessGroup({
						companyId: companyDomain.id,
						groupId: groupToDelete!,
					});
					if ("error" in result) {
						showErrorNotification({ message: t("Failed to delete the group") });
					} else {
						showSuccessNotification({ message: t("The group was deleted") });
						setGroupToDelete(undefined);
					}
				}}
				confirmButtonLabel={t("Remove group")}
				isLoading={isDeletingAccessGroup}
				destructive
			/>
		</>
	);
};
